/**
 * Theme Custom JavaScript
 *
 * Custom functionality for agoodcase.dk theme.
 *
 * Contents:
 * 1. Quick Add Button - Enables quick add functionality on product cards
 * 2. Pant System - Automatic bottle deposit management in cart
 */

// ############################################################################
// #                                                                          #
// #  1. QUICK ADD BUTTON                                                     #
// #                                                                          #
// #  Enables quick add functionality on product cards by triggering          #
// #  the hidden quick-add button when the visible add button is clicked.     #
// #                                                                          #
// ############################################################################

function initQuickAddButtons() {
  document.querySelectorAll('.product-card').forEach((card) => {
    const addButton = card.querySelector('.product-card-add-button');
    if (addButton) {
      addButton.addEventListener('click', (e) => {
        e.preventDefault();
        setTimeout(() => {
          card.querySelector('.product-card__quick-add-button')?.click();
        }, 200);
      });
    }
  });
}

// ############################################################################
// #                                                                          #
// #  2. PANT SYSTEM (Bottle Deposit)                                         #
// #                                                                          #
// #  Automatically manages bottle deposits (pant) in the cart.               #
// #  - Calculates required pant based on products in cart                    #
// #  - Supports selling plans (subscriptions)                                #
// #  - Syncs pant quantities when cart changes                               #
// #                                                                          #
// #  Pant is grouped by selling plan:                                        #
// #  - Products without subscription -> pant without subscription            #
// #  - Products with subscription -> pant with same subscription             #
// #                                                                          #
// ############################################################################

const PANT_CONFIG = {
  variantIds: {
    a: 48232119796049,
    b: 48232119828817,
    c: 48232119861585
  }
};

// ----- Pant Utilities -----

function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

async function cartFetch(endpoint, data) {
  try {
    const response = await fetch(window.Shopify.routes.root + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error(`Cart API error: ${response.status}`);
    return response;
  } catch (error) {
    console.error('Pant system:', error.message);
    return null;
  }
}

function getPantType(variantId) {
  for (const [type, id] of Object.entries(PANT_CONFIG.variantIds)) {
    if (id === variantId) return type;
  }
  return null;
}

// ----- Pant Calculation -----

/**
 * Calculate required pant from cart items, grouped by selling plan
 * @returns {Object} { 'none': {a:X, b:Y, c:Z}, 'planId': {a:X, b:Y, c:Z} }
 */
function calculateRequiredPant(items) {
  const pantByPlan = {};

  items.forEach((item) => {
    if (item.getAttribute('data-pant')) return;

    const quantity = parseInt(item.getAttribute('data-quantity')) || 0;
    const planId = item.getAttribute('data-selling-plan-id') || 'none';

    if (!pantByPlan[planId]) {
      pantByPlan[planId] = { a: 0, b: 0, c: 0 };
    }

    ['a', 'b', 'c'].forEach(type => {
      const pantQty = parseInt(item.getAttribute(`data-pant-${type}`)) || 0;
      pantByPlan[planId][type] += pantQty * quantity;
    });
  });

  return pantByPlan;
}

/**
 * Get existing pant from cart API, grouped by selling plan
 * @returns {Object} { 'none': {a: {quantity, key}}, 'planId': {...} }
 */
async function getExistingPant() {
  try {
    const response = await fetch(window.Shopify.routes.root + 'cart.js');
    if (!response.ok) throw new Error(`Cart fetch error: ${response.status}`);
    var cart = await response.json();
  } catch (error) {
    console.error('Pant system:', error.message);
    return null;
  }
  const existingPant = {};

  cart.items.forEach(item => {
    const pantType = getPantType(item.variant_id);
    if (!pantType) return;

    const planId = item.selling_plan_allocation?.selling_plan?.id?.toString() || 'none';

    if (!existingPant[planId]) existingPant[planId] = {};

    existingPant[planId][pantType] = {
      quantity: item.quantity,
      key: item.key
    };
  });

  return existingPant;
}

// ----- Pant Cart Operations -----

async function addPant(type, quantity, planId) {
  const item = { id: PANT_CONFIG.variantIds[type], quantity };
  if (planId !== 'none') item.selling_plan = parseInt(planId);
  await cartFetch('cart/add.js', { items: [item] });
}

async function updatePant(key, quantity) {
  await cartFetch('cart/change.js', { id: key, quantity });
}

async function removePant(key) {
  await cartFetch('cart/change.js', { id: key, quantity: 0 });
}

// ----- Pant Sync -----

/**
 * Sync pant in cart: add missing, update changed, remove excess
 */
async function syncPant(items, reload = false) {
  const required = calculateRequiredPant(items);
  const existing = await getExistingPant();
  if (!existing) return;
  let hasChanges = false;

  // Add/update pant for each selling plan
  for (const [planId, requiredPant] of Object.entries(required)) {
    const existingPant = existing[planId] || {};

    for (const type of ['a', 'b', 'c']) {
      const want = requiredPant[type];
      const have = existingPant[type];

      if (want > 0 && !have) {
        await addPant(type, want, planId);
        hasChanges = true;
      } else if (want > 0 && have && want !== have.quantity) {
        await updatePant(have.key, want);
        hasChanges = true;
      } else if (want === 0 && have) {
        await removePant(have.key);
        hasChanges = true;
      }
    }
  }

  // Remove orphaned pant (selling plan no longer in cart)
  for (const [planId, existingPant] of Object.entries(existing)) {
    if (!required[planId]) {
      for (const type of ['a', 'b', 'c']) {
        if (existingPant[type]) {
          await removePant(existingPant[type].key);
          hasChanges = true;
        }
      }
    }
  }

  // Refresh cart
  if (hasChanges) {
    if (reload) {
      window.location.replace('/');
    } else {
      setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh')), 200);
    }
  }
}

// ############################################################################
// #                                                                          #
// #  EVENT LISTENERS                                                         #
// #                                                                          #
// ############################################################################

// Page load
window.addEventListener('pageshow', async () => {
  initQuickAddButtons();

  // Sync pant on cart page
  const cartItems = document.querySelectorAll('.shopify-section--main-cart .order-summary .line-item');
  if (cartItems?.length > 0) {
    await syncPant(cartItems, true);
  }
});

// Cart drawer changes
const debouncedCartChange = debounce(async (e) => {
  for (const sectionHtml of Object.values(e.detail.cart.sections || {})) {
    if (sectionHtml) {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const cartItems = doc.querySelectorAll('.cart-drawer__items .line-item');
      if (cartItems.length > 0) {
        await syncPant(cartItems);
        return;
      }
    }
  }
}, 300);

document.addEventListener('cart:change', debouncedCartChange);
